'use client';

import { useContext, useState, FC } from 'react';
import { Button, ButtonGroup, Flex, Input, RolloverItem } from '@/_components';
import {
  deletePriorYearItem,
  getPriorYearItems,
  updatePriorYearItem,
} from '@/_api/admin-prior-year-items';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import type { RItemProps } from './RItem.types';

export const RItem: FC<RItemProps> = ({
  item,
  setPriorYearItems,
  companyId,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [itemName, setItemName] = useState(item.name);

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const onUpdatePriorYearItem = async () => {
    if (!itemName) {
      addToast('error', 'Please enter a name');
      return;
    }

    const params = {
      id: item.id,
      name: itemName,
      companyId: item.company_id,
      year: item.year,
    };

    try {
      setIsLoading(true);
      const response = (await updatePriorYearItem({ ...params, token })) as any;

      if (response.error) {
        throw new Error(response.exception);
      }

      const priorYearItemsFromResponse = await getPriorYearItems({
        ...params,
        token,
      });
      setPriorYearItems(priorYearItemsFromResponse);
      addToast('success', 'Prior year item updated');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
      setIsEditing(false);
    }
  };

  const onDeletePriorYearItem = async () => {
    try {
      setIsLoading(true);
      const response = (await deletePriorYearItem({
        id: item.id,
        companyId: item.company_id,
        token,
      })) as any;

      if (response.error) {
        throw new Error(response.exception);
      }

      const priorYearItemsFromResponse = await getPriorYearItems({
        companyId: item.company_id,
        token,
      });
      setPriorYearItems(priorYearItemsFromResponse);
      addToast('success', 'Prior year item deleted');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onEdit = () => setIsEditing(true);

  return !isEditing ? (
    <RolloverItem
      item={{ ...item, name: itemName }}
      onEdit={onEdit}
      onDelete={onDeletePriorYearItem}
      companyId={companyId}
    />
  ) : (
    <Flex gap={2} isYCentered={true} className="wp-100">
      <Input
        label="Name"
        isLabelHidden={true}
        value={itemName}
        onChange={(e) => setItemName(e.target.value)}
        type="text"
      />
      <ButtonGroup>
        <Button
          onClick={onUpdatePriorYearItem}
          isDisabled={isLoading}
          value="Save"
          intent="primary"
        />
      </ButtonGroup>
    </Flex>
  );
};
